import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { ViewUIModel } from './models/view-ui.model';
import { ViewState, ViewStateModel } from './state/view.state';
import { ViewUIStatus } from './models/view-ui-status.enum';
import { ViewUI } from './models/view-ui.enum';
import { StoryLabelsState } from '../story/state/labels/story.labels.state';
import { StoryChaptersState } from '../story/state/chapters/story.chapters.state';
import { get, trim } from 'lodash';
import { environment } from 'src/environments/environment';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { Breakpoint } from '../breakpoint/models/breakpoint.enum';
import { BreakpointState } from '../breakpoint/state/breakpoint.state';
import { ViewWindow } from './models/view-window.enum';
import { ViewLoaderEnum } from './models/view-loader.enum';
import { ChapterService } from '../story/chapter.service';
import { ViewLayoutConfig } from './models/view-layout-config.enum';
import { QuizState } from '../quiz/state/quiz.state';
import { ContentBlockTypes } from '../content-blocks/models/content-block-types';
import { LayoutState } from '../layout/state/layout.state';
import { SiteHeaderType } from '../layout/models/site-header-type.enum';
import { ViewLayout } from './models/view-layout';

@Injectable({
  providedIn: 'root'
})
export class ViewUIService {
  ui: ViewUIModel = null;
  breakpoint: Breakpoint = null;

  constructor(
    private store: Store,
    private chapterService: ChapterService
  ) { }

  getBase() {
    return {
      ui: this.store.selectSnapshot(ViewState.getUI),
      layout: this.store.selectSnapshot(ViewState.getLayout),
      breakpoint: this.store.selectSnapshot(BreakpointState.getBreakpoint),
      hideUI: this.isUIHidden()
    };
  }

  isUIHidden() {
    const openWindow = this.store.selectSnapshot(ViewState.getOpenWindow);
    const chapterIndex = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterIndex);
    const ui = this.store.selectSnapshot(ViewState.getUI);
    const isCircleLoaderOpen = (openWindow === ViewWindow.Loader && chapterIndex <= 0 && ui.loader.template === ViewLoaderEnum.Circle);

    return isCircleLoaderOpen
      || openWindow === ViewWindow.OnDemand
      || openWindow === ViewWindow.Interact
      || openWindow === ViewWindow.Tutorial
      || openWindow === ViewWindow.Whiteboard;
  }

  isDrawerVisible() {
    const { hideUI, ui, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    // Large breakpoint, never show unless manually specified with `ui-layout` parameter
    // When 'drawer' parameter is specified, bypass this check
    // Do show drawer in portrait mode, even at large sizes
    if (layout !== ViewLayoutConfig.DrawerOnly) {
        if (this.getLayoutFromWindowSize() !== ViewLayout.Drawer) {
          return false;
        }
    }


    // Show for loft & studio
    if (environment.application !== EnvironmentApplication.Viewer) {
      return true;
    }
    
    // Medium & Small breakpoint
    const visible = 
      (ui.drawer.enable && this.getVisibleContent() !== 'none')
      || ui.nodeInfo.status === ViewUIStatus.Show
      || ui.labelInfo.status === ViewUIStatus.Show;
    
    return visible;
  }

  isColumnVisible() {
    const { hideUI, layout, ui, breakpoint } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    // Don't show when drawer only is specified as a layout
    if (layout === ViewLayoutConfig.DrawerOnly) {
      return false;
    }

    // Hide on collapse
    if (ui.column.status === ViewUIStatus.Collapse) {
      return false;
    }

    if (this.getLayoutFromWindowSize() !== ViewLayout.Column) {
      return false;
    }

    // Default ui (description)
    return (ui.column.enable && this.getVisibleContent() === 'full');
  }
  
  isColumnToggleVisible() {
    const { hideUI, layout, ui, breakpoint } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    // Hide when quizzing active
    const quizActive = this.store.selectSnapshot(QuizState.getQuizModeActive);
    if (quizActive) {
      return false;
    }

    // Below large breakpoint, never show unless manually specified with `ui-layout` parameter
    switch (layout) {
      case ViewLayoutConfig.Default:
        if (this.getLayoutFromWindowSize() !== ViewLayout.Column) {
          return false;
        }
        break;
      case ViewLayoutConfig.DrawerOnly:
        return false;
    }

    return (ui.column.enable && this.getVisibleContent() === 'full');
  }

  isTitleOnlyVisible() {
    const { hideUI, ui, breakpoint } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    // Large breakpoint, never show
    if (breakpoint < Breakpoint.Large) {
      return false;
    }

    // Only active on large screen, and when the only visible content in the column would have been the chapter title
    return (ui.titleOnly.enable && this.getVisibleContent() === 'titleOnly');
  }

  isToolsVisible() {
    const { hideUI, ui, breakpoint } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    return this.isUIVisible(ViewUI.Tools);
  }

  isToolbarVisible() {
    const { hideUI, ui } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    return ui.toolbar.enable;
  }

  isLogoVisible() {
    const { ui } = this.getBase();

    if (this.store.selectSnapshot(ViewState.getOpenWindow) === ViewWindow.OnDemand) {
      return false;
    }

    return ui.logo.enable;
  }

  isCameraVisible() {
    const { hideUI, ui } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    return ui.camera.enable;
  }

  isPaginationVisible() {
    const { hideUI, ui } = this.getBase();
    
    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    if (this.store.selectSnapshot(StoryChaptersState.getChapterCount) <= 1) {
      return false;
    }

    return ui.pagination.enable;
  }

  isMediaVisible() {
    const { hideUI, ui } = this.getBase();  

    if (hideUI) {
      return false;
    }

    const isAnimated = this.store.selectSnapshot(StoryChaptersState.getIsChapterAnimated);
    const isEnabled = ui.media.enable;

    return isAnimated && isEnabled;
  }

  isMediabarVisible() {
    const { hideUI } = this.getBase();  

    if (hideUI) {
      return false;
    }

    // Visible when audio or animation is visible
    const animationVisible = this.isMediaVisible();
    const audioVisible = this.isAudioVisible();

    return animationVisible || audioVisible;
  }

  isMenuVisible() {
    const { hideUI, ui, breakpoint } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    const visible = 
      ui.fullscreen.enable
      || ui.help.enable
      || ui.search.enable
      || ui.tools.enable
      || ui.whiteboard.enable
      || ui.tutorial.enable;

    return visible && ui.menu.enable;
  }

  isNodeInfoColumnVisible() {
    const { hideUI, ui, breakpoint } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    if (breakpoint >= Breakpoint.Large && this.isUIVisible(ViewUI.NodeInfo)) {
      return true;
    }
    
    // Medium / Small, defer to drawer enabled
    if (breakpoint < Breakpoint.Large) {
      return !ui.drawer.enable && this.isUIVisible(ViewUI.NodeInfo);
    }
  }

  isNodeInfoVisible(location: ViewLayout) {
    const { hideUI, ui, breakpoint } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }

    // Large, never show
    if (breakpoint >= Breakpoint.Large && location === ViewLayout.Column) {
      return this.isUIVisible(ViewUI.NodeInfo);
    }

    // Medium / Small, drawer and column
    if (breakpoint < Breakpoint.Large && location === ViewLayout.Drawer) {
      return this.isUIVisible(ViewUI.NodeInfo);
    }

    return false;
  }

  isAudioVisible() {
    const { ui } = this.getBase();
    const hasAudio = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterAudio) != null;

    return ui.audio.enable && hasAudio;
  }

  isSearchColumnVisible() {
    const { hideUI, ui, breakpoint } = this.getBase();

    // Hide when loader is active
    if (hideUI) {
      return false;
    }
    
    return this.isUIVisible(ViewUI.Search);
  }

  isUndoVisible() {
    const { hideUI, layout, ui, breakpoint } = this.getBase();
  
    if (hideUI) {
      return false;
    }

    return ui.undo.enable;
  }

  isVisibilityLayersVisible() {
    const { hideUI, ui } = this.getBase();  

    if (hideUI) {
      return false;
    }

    const isEnabled = ui.visibilityLayers.enable;
    const visibilityLayers = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterCurrentLayers);
    return isEnabled && this.chapterService.hasVisibilityLayers(visibilityLayers);
  }

  isVisible(ui: ViewUI) {
    switch (ui) {
      case ViewUI.Audio:
        return this.isAudioVisible();

      case ViewUI.Camera:
        return this.isCameraVisible();

      case ViewUI.Column:
        return this.isColumnVisible();

      case ViewUI.ColumnToggle:
          return this.isColumnToggleVisible();

      case ViewUI.Drawer:
        return this.isDrawerVisible();

      case ViewUI.Logo:
        return this.isLogoVisible();

      case ViewUI.Media:
        return this.isMediaVisible();

      case ViewUI.Mediabar:
        return this.isMediabarVisible();

      case ViewUI.Menu:
        return this.isMenuVisible();

      case ViewUI.Pagination:
        return this.isPaginationVisible();

      case ViewUI.Search:
        return this.isSearchColumnVisible();

      case ViewUI.TitleOnly:
        return this.isTitleOnlyVisible();

      case ViewUI.Tools:
        return this.isToolsVisible();

      case ViewUI.Toolbar:
        return this.isToolbarVisible();

      case ViewUI.Undo:
        return this.isUndoVisible();

      case ViewUI.VisibilityLayers:
        return this.isVisibilityLayersVisible();

      default:
        return this.isUIVisible(ui);

    }
  }

  isUIVisible(_ui: ViewUI) {
    const { hideUI, ui } = this.getBase();

    const el = ui[_ui];
    
    if (!el) {
      console.warn('Attempting to check visibility of a non-existent element: ${element}');
      return false;
    }

    return !hideUI && 
      ui[_ui].enable && 
      ui[_ui].status !== ViewUIStatus.Hide;
  }

  isUIEnabled(_ui: ViewUI) {
    const { ui } = this.getBase();
    return ui[_ui].enable;
  }

  // Determines what content is visible
  getVisibleContent(): 'full' | 'titleOnly' | 'none' {
    const { ui } = this.getBase();
    const labels = this.store.selectSnapshot(StoryLabelsState.getChapterLabels);
    const selectedChapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
    const description = get(selectedChapter, 'info.description');
    const displayName = get(selectedChapter, 'info.displayName');
    const legend = get(selectedChapter, 'legend', []);

    const contentBlockVisible = get(selectedChapter, 'contentBlocks', []).length;
    const labelsVisible = ((ui.labelList.enable && labels.length > 0));
    const descriptionVisible = (description != null && trim(description).length > 0);
    const titleVisible = (displayName != null && trim(displayName).length > 0);
    const legendVisible = ui.legend.enable && legend.length > 0;
    const quizVisible = this.store.selectSnapshot(QuizState.getQuizModeActive);

    const isFullContentVisible = labelsVisible || descriptionVisible || legendVisible || quizVisible || contentBlockVisible;

    if (isFullContentVisible) {
      return 'full';
    }

    if (titleVisible) {
      return 'titleOnly';
    }

    return 'none';
  }

  /**
   * Rules
   * 1. Portrait mode (taller than wider) always shows drawer
   * 2. Always attempt to maintain minimum 320px vertical and horizontal usable space in 3D scene
   */
  getLayoutFromWindowSize(): ViewLayout {
    const { width, height } = document.body.getBoundingClientRect();
    const orientation = width / height >= 1 ? 'landscape' : 'portrait';
    const { breakpoint } = this.getBase();

    // Always render drawer in protrait mode
    if (orientation === 'portrait') {
      return ViewLayout.Drawer;
    }

    // Account for site header on Studio, Loft
    const headerSize = environment.application === EnvironmentApplication.Viewer ? 0 : 56;

    // Magic numbers, written out for clarity
    const ySceneMin = 384 + headerSize; // 320px scene, 64px drawer, (optional) 56px header
    const xSceneMin = 708; // 320px scene, 320px column, 52px camera bar, 16px camera bar padding

    if (
      breakpoint >= Breakpoint.Large 
      || (height <= ySceneMin && width >= xSceneMin)) {
      return ViewLayout.Column;
    } else {
      return ViewLayout.Drawer;
    }
  }
}
